import React from "react";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate("/app");
  };

  // Always start notes from below the viewport (top: 105%) and animate upward
  const FloatingNote = ({ delay, duration, size, left }) => (
    <div
      className="floating-note"
      style={{
        left: `${left}%`,
        top: `105%`, // always start below the viewport
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        fontSize: `${size}rem`,
      }}
    >
      ♪
    </div>
  );

  return (
    <>
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .home-container {
          min-height: 100%;
          width: 100%;
          background: transparent;
          position: relative;
          overflow: hidden;

          font-family: "Segoe UI", "Roboto", "Arial", sans-serif;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 0 20px;
          transition: background 2s ease-in-out;
        }

        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .floating-note {
          position: absolute;
          color: rgba(102, 126, 234, 0.18);
          animation: float linear infinite;
          pointer-events: none;
          font-weight: bold;
          opacity: 0.95;
          filter: blur(0.5px) drop-shadow(0 0 12px #764ba2cc);
          z-index: 0;
          /* Start below the viewport */
          top: 105%;
          will-change: top, opacity;
        }

        .main-content {
          position: relative;
          z-index: 2;
        }

        @keyframes float {
          0% {
            top: 105%;
            opacity: 0;
            transform: rotate(0deg);
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            top: -10%;
            opacity: 0;
            transform: rotate(360deg);
          }
        }

        .header-section {
          text-align: center;
          margin-bottom: 50px;
        }

        .home-title {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 800;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 20px;
          animation: titleGlow 3s ease-in-out infinite alternate;
        }

        @keyframes titleGlow {
          0% {
            filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.3));
          }
          100% {
            filter: drop-shadow(0 0 20px rgba(118, 75, 162, 0.5));
          }
        }

        .home-subtitle {
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          color: #555;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
          animation: fadeInUp 1s ease-out 0.5s both;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .sections-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          margin-bottom: 50px;
        }

        .home-section {
          background: rgba(255, 255, 255, 0.8);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transform: translateY(20px);
          opacity: 0;
          animation: sectionSlideIn 0.8s ease-out forwards;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .home-section:nth-child(1) {
          animation-delay: 0.2s;
        }
        .home-section:nth-child(2) {
          animation-delay: 0.4s;
        }
        .home-section:nth-child(3) {
          animation-delay: 0.6s;
        }

        .home-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, transparent, #667eea, transparent);
          transition: left 0.5s ease;
        }

        .home-section:hover::before {
          left: 100%;
        }

        .home-section:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        @keyframes sectionSlideIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .home-section h2 {
          color: #1e2a47;
          font-size: 1.8rem;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .section-icon {
          font-size: 1.5rem;
          animation: iconBounce 2s ease-in-out infinite;
        }

        @keyframes iconBounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        .home-section ul {
          list-style: none;
          padding: 0;
        }

        .home-section li {
          margin-bottom: 15px;
          font-size: 1.1rem;
          color: #3d465c;
          padding-left: 25px;
          position: relative;
          line-height: 1.5;
        }

        .home-section li::before {
          content: "▶";
          position: absolute;
          left: 0;
          color: #667eea;
          font-size: 0.8rem;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .cta-section {
          text-align: center;
          animation: fadeInUp 1s ease-out 1s both;
          margin-bottom: 40px;
        }

        .proceed-btn {
          position: relative;
          display: inline-block;
          padding: 14px 32px;
          font-size: 1.08rem;
          font-weight: 800;
          letter-spacing: 0.03em;
          border: none;
          border-radius: 28px;
          color: #fff;
          background: linear-gradient(
            90deg,
            #8f6ed5,
            #6a82fb,
            #fc5c7d,
            #43cea2
          );
          background-size: 300% 300%;
          box-shadow: 0 0 18px 3px #8f6ed555, 0 2px 14px 0 #6a82fb55;
          cursor: pointer;
          outline: none;
          overflow: hidden;
          transition: transform 0.18s cubic-bezier(0.4, 2, 0.6, 1),
            box-shadow 0.3s, background-position 1.2s;
          animation: btnGradientMove 5s ease-in-out infinite;
        }

        @keyframes btnGradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .proceed-btn::after {
          content: "";
          position: absolute;
          left: 50%;
          top: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.18);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.4s, height 0.4s, opacity 0.4s;
          opacity: 0;
          pointer-events: none;
          z-index: 0;
        }

        .proceed-btn:hover {
          transform: scale(1.07) translateY(-2px);
          box-shadow: 0 0 48px 12px #fc5c7d55, 0 8px 40px 0 #43cea255;
          background-position: 100% 50%;
        }

        .proceed-btn:active::after {
          width: 240px;
          height: 240px;
          opacity: 0.3;
        }

        .proceed-btn:active {
          transform: scale(0.97) translateY(2px);
        }

        .home-footer {
          margin-top: 40px;
          margin-bottom: 0;
          text-align: center;
          color: #fff;
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          background: rgba(36, 44, 77, 0.38);
          box-shadow: 0 4px 32px 0 rgba(102, 126, 234, 0.08);
          border-radius: 22px;
          padding: 18px 30px;
          max-width: 620px;
          margin-left: auto;
          margin-right: auto;
          backdrop-filter: blur(8px) saturate(1.3);
          -webkit-backdrop-filter: blur(8px) saturate(1.3);
          animation: footerFadeIn 1.2s cubic-bezier(0.4, 2, 0.6, 1) 0.7s both;
          transition: background 0.3s;
        }

        @keyframes footerFadeIn {
          from {
            opacity: 0;
            transform: translateY(32px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .footer-emoji {
          display: inline-block;
          font-size: 1.4em;
          margin: 0 10px 0 0;
          vertical-align: middle;
          animation: rotate 3s linear infinite;
        }

        .footer-emoji {
          display: inline-block;
          animation: rotate 3s linear infinite;
          margin: 0 8px;
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .content-wrapper {
            padding: 40px 30px;
            margin: 20px;
          }

          .sections-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .home-section {
            padding: 25px 20px;
          }

          .proceed-btn {
            padding: 15px 30px;
            font-size: 1.1rem;
          }
        }
      `}</style>

      <div className="home-container">
        {/* Large, prominent floating musical notes */}
        {[...Array(10)].map((_, i) => (
          <FloatingNote
            key={i}
            delay={i * 1.2}
            duration={12 + Math.random() * 12}
            size={3.5 + Math.random() * 3.5} // much bigger notes
            left={Math.random() * 100}
          />
        ))}

        <main className="main-content">
          <div className="header-section">
            <h1 className="home-title">🎵 MoodTunes</h1>
            <p className="home-subtitle">
              Transform how you experience music! Our innovative full-stack
              application generates personalized playlists tailored to your
              emotional state, powered by cutting-edge NLP and sentiment
              classification.
            </p>
          </div>
          <div className="cta-section">
            <button className="proceed-btn" onClick={handleNavigate}>
              🚀 Start Your Music Journey
            </button>
          </div>

          <div className="sections-grid">
            <section className="home-section">
              <h2>
                <span className="section-icon">🧠</span>
                How It Works
              </h2>
              <ul>
                <li>
                  <strong>Emotion Analysis:</strong> Real-time mood detection
                  using a fine-tuned Logistic Regression model for rapid,
                  accurate classification.
                </li>
                <li>
                  <strong>Personalized Playlists:</strong> Instantly receive
                  curated music suggestions aligned with your emotions,
                  leveraging the Jamendo API.
                </li>
                <li>
                  <strong>Seamless Experience:</strong> Asynchronous RESTful API
                  calls ensure fluid, engaging interactions.
                </li>
              </ul>
            </section>

            <section className="home-section">
              <h2>
                <span className="section-icon">⚡</span>
                Technology Stack
              </h2>
              <ul>
                <li>
                  <strong>Backend:</strong> FastAPI (Hugging Face Spaces),
                  NestJS, Prisma ORM, PostgreSQL, GCP Cloud Run, Docker,
                  Supabase
                </li>
                <li>
                  <strong>Frontend:</strong> React, Redux Toolkit, React Router
                </li>
                <li>
                  <strong>Security:</strong> Firebase Auth, JWT
                </li>
                <li>
                  <strong>Music API:</strong> Jamendo
                </li>
              </ul>
            </section>

            <section className="home-section">
              <h2>
                <span className="section-icon">✨</span>
                Features
              </h2>
              <ul>
                <li>
                  Dynamic, responsive interface for a delightful user experience
                </li>
                <li>Secure, email-based user authentication</li>
                <li>Cloud scalability and data integrity</li>
                <li>Emotionally resonant music discovery journey</li>
              </ul>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default Home;
